import { statusGraphqlService, patientsGraphqlService, usersGraphqlService, groupsGraphqlService, clinicsGraphqlService } from '@/services/api';
import { graphqlClient } from '@/services/core/graphqlClient';
import { ESEnrollmentService } from '@/services/features/ESEnrollmentService';
import { ESPatientTransferService } from '@/services/features/ESPatientTransferService';
import { PatientTimelogService } from '@/services/features/PatientTimelogService';
import { CallHandlerClinicNumber, CallHandlerClinicNumberSource } from '@/types/clinicTypes';

export interface PatientData {
  first_name: string;
  last_name: string;
  dob: string;
  gender: string;
  mobile_phone_number: string;
  home_phone_number: string;
  email: string;
  address_line_1: string;
  city: string;
  state: string;
  zip_code: string;
  clinic_name: string;
  provider_id: number;
  provider_name: string;
  facility_name?: string;
  clinicFacilityId?: number;
  primary_dx: string;
  secondary_dx: string;
  primary_insurance_provider: string;
  primary_insurance_id: string;
  secondary_insurance_provider: string;
  secondary_insurance_id: string;
  emr_id: string;
  ccm_status: number;
  patient_clinic_timezone?: string;
  clinic_id?: number;
  pep_patient_id?: number;
  state_id?: number;
  emergency_first_name?: string;
  emergency_last_name?: string;
  emergency_mobile_phone?: string;
  // Enrollment attempts from REST API (legacy - specific program types)
  ccm_status_updates?: any[];
  rpm_status_updates?: any[];
  // Dynamic enrollment attempts by program type (CCM, RPM, PCM, etc.)
  enrollmentAttemptsByProgramType?: Record<string, any[]>;
  // Program eligibility flags
  rpmEligible?: boolean;
  pcmEligible?: boolean;
  ccmEligible?: boolean;
}

export interface CCMStatusUpdate {
  // Old GraphQL fields
  id?: number;
  status?: string;
  comment?: string;
  user_id?: number;
  timestamp?: string;
  
  // New REST API fields
  enrollmentUpdateId?: number;
  enrollmentPatientId?: number;
  programType?: number;
  programStatusId?: number;
  workflowStatusId?: number;
  taskId?: number;
  communicationTypeId?: number | null;
  groupId?: number;
  communicationAttempt?: number;
  updatedBy?: number;
  updatedDatetime?: string;
  programTypeName?: string;
  programStatusName?: string;
  workflowStatusName?: string;
  taskName?: string;
  communicationTypeName?: string;
  updatedByName?: string;
  formattedDateTime?: string;
}

export interface CCMStatusOption {
  id: number;
  status: string;
}

export interface GroupOption {
  group_id: number;
  group_name: string;
}

export class ESPatientService {
  private esEnrollmentService: ESEnrollmentService;
  private esPatientTransferService: ESPatientTransferService;
  private patientTimelogService: PatientTimelogService;

  constructor(
    private userId: string,
    private hasuraToken?: string,
    private userType?: string,
    private token?: string
  ) {
    // Initialize feature services with authentication context
    this.esEnrollmentService = new ESEnrollmentService({
      userId: this.userId,
      hasuraToken: this.hasuraToken,
      userType: this.userType,
      token: this.token
    });

    this.esPatientTransferService = new ESPatientTransferService({
      userId: this.userId,
      hasuraToken: this.hasuraToken,
      userType: this.userType,
      token: this.token
    });

    this.patientTimelogService = new PatientTimelogService({
      userId: this.userId,
      hasuraToken: this.hasuraToken,
      userType: this.userType,
      token: this.token
    });
  }

  // Update authentication context for all feature services
  updateAuthContext(hasuraToken: string, userType?: string, token?: string) {
    this.hasuraToken = hasuraToken;
    this.userType = userType;
    this.token = token;

    const authContext = {
      userId: this.userId,
      hasuraToken: this.hasuraToken,
      userType: this.userType,
      token: this.token
    };

    this.esEnrollmentService.updateContext(authContext);
    this.esPatientTransferService.updateContext(authContext);
    this.patientTimelogService.updateContext(authContext);
  }

  // REMOVED: CCM status options no longer needed - using program type status mapping instead
  // async loadCCMStatusOptions(): Promise<CCMStatusOption[]> {
  //   ...
  // }

  // Use proper API service layer - fetch distinct user groups from workflow assignments
  async loadGroupOptions(): Promise<GroupOption[]> {
    try {
      const query = `
        query GetESGroupOptions($userId: Int!) {
          pep_vw_patient_workflow_user_assignments(
            where: {assigned_user_id: {_eq: $userId}}
            distinct_on: user_group_id
          ) {
            user_group_id
            user_group_name
          }
        }
      `;
      
      const response = await graphqlClient.query<{ 
        pep_vw_patient_workflow_user_assignments: Array<{ 
          user_group_id: number; 
          user_group_name: string;
        }>;
      }>(
        query,
        { userId: parseInt(this.userId) },
        this.hasuraToken!
      );
      
      // Map to GroupOption format
      return response.data?.pep_vw_patient_workflow_user_assignments?.map(item => ({
        group_id: item.user_group_id,
        group_name: item.user_group_name
      })) || [];
    } catch (error) {
      console.error('Error loading group options:', error);
      return [];
    }
  }

  // Use centralized GraphQL API for clinic numbers
  async loadClinicNumbers(clinicId: number, stateId: number): Promise<CallHandlerClinicNumber[]> {
    try {
      if (!this.hasuraToken) {
        console.warn('No Hasura token available for clinic numbers query');
        return [];
      }

      const response = await clinicsGraphqlService.getClinicNumbers(clinicId, stateId, this.hasuraToken);

      // Transform the response to match the expected format for CallHandler
      return response.map(item => ({
        phone_number: item.phone_number,
        number_source_id: item.number_source_id,
        origin_number_type_id: item.origin_number_type_id,
        is_active: item.is_active,
        is_global: item.is_global,
        clinic_mapped: item.clinic_mapped
      }));
    } catch (error) {
      console.error('Error loading clinic numbers:', error);
      return [];
    }
  }

  // Load call numbers using REST API (for call functionality)
  async loadCallNumbers(clinicId: number): Promise<CallHandlerClinicNumber[]> {
    try {
      if (!this.token) {
        console.warn('No token available for call numbers query');
        return [];
      }

      const response = await clinicsGraphqlService.getOutboundNumbers(clinicId, 'call', this.token, 'Enrollment');
      
      // Map number sources to IDs
      const sourceToId: Record<string, number> = {
        'Twilio': 1,
        'Clinic': 2,
        'Global': 3
      };

      // Transform to CallHandler format
      return response.outboundNumber.map((item) => {
        const sourceId = sourceToId[item.numberSource] || 99;
        return {
          phone_number: item.originNumber,
          number_source_id: sourceId,
          origin_number_type_id: 1,
          is_active: true,
          is_global: item.numberSource === 'Global',
          clinic_mapped: []
        };
      });
    } catch (error) {
      console.error('Error loading call numbers:', error);
      return [];
    }
  }

  // Load SMS numbers using REST API (for SMS functionality)
  async loadSmsNumbers(clinicId: number): Promise<CallHandlerClinicNumber[]> {
    try {
      if (!this.token) {
        console.warn('No Hasura token available for SMS numbers query');
        return [];
      }

      const response = await clinicsGraphqlService.getOutboundNumbers(clinicId, 'sms', this.token, 'Enrollment');
      
      // Map number sources to IDs
      const sourceToId: Record<string, number> = {
        'Twilio': 1,
        'Clinic': 2,
        'Global': 3
      };

      // Transform to CallHandler format
      return response.outboundNumber.map((item) => {
        const sourceId = sourceToId[item.numberSource] || 99;
        return {
          phone_number: item.originNumber,
          number_source_id: sourceId,
          origin_number_type_id: 1,
          is_active: true,
          is_global: item.numberSource === 'Global',
          clinic_mapped: []
        };
      });
    } catch (error) {
      console.error('Error loading SMS numbers:', error);
      return [];
    }
  }

  /**
   * Generate source options from loaded numbers
   * Returns unique sources with proper IDs
   */
  generateSourceOptions(numbers: CallHandlerClinicNumber[]): CallHandlerClinicNumberSource[] {
    const sourceToId: Record<string, number> = {
      'Twilio': 1,
      'Clinic': 2,
      'Global': 3,
      'Ported': 4
    };

    // Get unique sources from the numbers
    const uniqueSources = new Set<number>();
    numbers.forEach(num => {
      if (num.number_source_id) {
        uniqueSources.add(num.number_source_id);
      }
    });

    // Map back to source names
    const idToSource: Record<number, string> = {
      1: 'Twilio',
      2: 'Clinic',
      3: 'Global',
      4: 'Ported'
    };

    const sources: CallHandlerClinicNumberSource[] = [];
    uniqueSources.forEach(sourceId => {
      const sourceName = idToSource[sourceId];
      if (sourceName) {
        sources.push({
          number_source: sourceName,
          number_source_id: sourceId
        });
      }
    });

    return sources;
  }

  // Use centralized GraphQL API for number source types
  async loadNumberSourceTypes(): Promise<CallHandlerClinicNumberSource[]> {
    try {
      if (!this.hasuraToken) {
        console.warn('No Hasura token available for number source types query');
        return [];
      }

      const response = await clinicsGraphqlService.getClinicNumberSourceTypes(this.hasuraToken);

      // Transform the response to match the expected format for CallHandler
      return response
        .filter(item => !item.is_deleted)
        .map(item => ({
          number_source: item.number_source,
          number_source_id: item.number_source_id
        }));
    } catch (error) {
      console.error('Error loading number source types:', error);
      return [];
    }
  }


  // Delegate to feature service for patient data - always fetches fresh using REST API
  async getPatientData(): Promise<{ 
    patientData: PatientData | null; 
    workflowId: number | null;
    assignmentData: any;
    noPatientMessage?: string;
  }> {
    console.log('[ESPatientService] getPatientData called - fetching fresh data via REST API');
    try {
      // Step 1: Get visible patient via REST API (replaces GraphQL query)
      const visiblePatientResponse = await this.esEnrollmentService.getVisiblePatient();
      
      if (!visiblePatientResponse.success || !visiblePatientResponse.data) {
        console.warn('ESPatientService.getPatientData: No visible patient found for ES user');
        return { 
          patientData: null, 
          workflowId: null, 
          assignmentData: null,
          noPatientMessage: visiblePatientResponse.message 
        };
      }

      const assignment = visiblePatientResponse.data;
      // Use enrollmentPatientId from the API response (new field name from /visiblePatient API)
      const enrollmentPatientId = assignment.enrollmentPatientId;

      if (!enrollmentPatientId) {
        console.warn('ESPatientService.getPatientData: No enrollmentPatientId in visible patient response');
        return { patientData: null, workflowId: null, assignmentData: null };
      }

      console.log('[ESPatientService] Found visible patient:', { 
        enrollmentPatientId: enrollmentPatientId, 
        workflowId: assignment.workflowId,
        activityId: assignment.activityId,
        userGroupId: assignment.userGroupId
      });

      // Extract workflow_id from assignment (use workflowId from API response)
      const workflowId = assignment.workflowId || null;

      // Step 2: Use enrollmentPatientId to fetch full enrollment details via REST API
      console.log('[ESPatientService] Fetching enrollment details for enrollmentPatientId:', enrollmentPatientId);
      const detailsResponse = await this.esEnrollmentService.getPatientEnrollmentDetails(enrollmentPatientId);

      if (!detailsResponse.success || !detailsResponse.data) {
        console.error('ESPatientService.getPatientData: Failed to fetch enrollment details', detailsResponse);
        return { patientData: null, workflowId, assignmentData: assignment };
      }

      console.log('[ESPatientService] Successfully fetched patient enrollment details');

      // Return enrollment details, workflow_id, and full assignment data
      return { 
        patientData: detailsResponse.data as PatientData, 
        workflowId,
        assignmentData: assignment
      };
    } catch (error) {
      console.error('Error getting patient data:', error);
      return { patientData: null, workflowId: null, assignmentData: null };
    }
  }

  // Fetch ONLY enrollment details (no assignment query) - for post-save refresh
  async getEnrollmentDetailsOnly(patientId: number): Promise<PatientData | null> {
    console.log('[ESPatientService] getEnrollmentDetailsOnly called for patient:', patientId);
    try {
      const detailsResponse = await this.esEnrollmentService.getPatientEnrollmentDetails(patientId);

      if (!detailsResponse.success || !detailsResponse.data) {
        console.error('ESPatientService.getEnrollmentDetailsOnly: Failed to fetch enrollment details', detailsResponse);
        return null;
      }

      console.log('[ESPatientService] Successfully fetched enrollment details only');
      return detailsResponse.data as PatientData;
    } catch (error) {
      console.error('Error getting enrollment details only:', error);
      return null;
    }
  }

  // Load specific patient by patient_id (for external navigation to VPE)
  // userType parameter determines if assignment query should be called (only for ES users)
  async getSpecificPatientData(patientId: number, userType?: string): Promise<{
    patientData: PatientData | null;
    workflowId: number | null;
    assignmentData: any;
  }> {
    console.log('[ESPatientService] getSpecificPatientData called for patient_id:', patientId, 'userType:', userType);
    try {
      // Step 1: Query assignment data ONLY for ES (VPE) users
      let assignment: any = null;
      let workflowId: number | null = null;

      // Only call the assignment GraphQL query for ES (VPE) users
      if (userType === 'ES') {
        try {
          const esUserId = parseInt(String(this.userId));
          const assignmentResponse = await this.esEnrollmentService.getPatientAssignmentByPatientId(esUserId);
          const assignments = assignmentResponse.data?.pep_vw_patient_workflow_user_assignments;
          assignment = assignments && assignments.length > 0 ? assignments[0] : null;

          if (assignment) {
            console.log('[ESPatientService] Found patient assignment via userId:', {
              patientId: assignment.patient_id,
              workflowId: assignment.workflow_id,
              userId: esUserId,
            });
            workflowId = assignment.workflow_id || null;
          } else {
            console.warn('ESPatientService.getSpecificPatientData: No assignment found for userId:', esUserId);
          }
        } catch (assignmentError) {
          console.warn('ESPatientService.getSpecificPatientData: Assignment lookup failed, continuing with REST only', assignmentError);
        }
      } else {
        console.log('[ESPatientService] Skipping assignment query for non-ES user type:', userType);
      }

      // Step 2: Use patient_id to fetch full enrollment details via REST API (always attempt)
      console.log('[ESPatientService] Fetching enrollment details for patient:', patientId);
      const detailsResponse = await this.esEnrollmentService.getPatientEnrollmentDetails(patientId);

      if (!detailsResponse.success || !detailsResponse.data) {
        console.error('ESPatientService.getSpecificPatientData: Failed to fetch enrollment details', detailsResponse);
        return { patientData: null, workflowId, assignmentData: assignment };
      }

      console.log('[ESPatientService] Successfully fetched specific patient enrollment details');

      // Merge clinic_id from assignment data if not present in patient data
      const patientData = detailsResponse.data as PatientData;
      if (!patientData.clinic_id && assignment?.clinic_id) {
        patientData.clinic_id = assignment.clinic_id;
      }

      // Return enrollment details, workflow_id (may be null), and assignment data (may be null)
      return {
        patientData,
        workflowId,
        assignmentData: assignment,
      };
    } catch (error) {
      console.error('ESPatientService.getSpecificPatientData: Unexpected error:', error);
      return { patientData: null, workflowId: null, assignmentData: null };
    }
  }


  // REMOVED: CCM status updates no longer needed - enrollment attempts now come from REST API
  // async getCCMStatusUpdates(patientId: number): Promise<CCMStatusUpdate[]> {
  //   ...
  // }

  // Delegate to feature service for patient locking
  async lockPatientByGroup(groupId: number): Promise<boolean> {
    try {
      const response = await this.esEnrollmentService.lockPatientByGroup(this.userId, groupId);
      return response.success;
    } catch (error) {
      console.error('Error locking patient by group:', error);
      return false;
    }
  }

  // Delegate to feature service for patient unlocking
  async unlockPatient(patientClinicDataId: number): Promise<boolean> {
    try {
      const response = await this.esEnrollmentService.unlockPatient(this.userId, patientClinicDataId);
      return response.success;
    } catch (error) {
      console.error('Error unlocking patient:', error);
      return false;
    }
  }

  // Delegate to feature service for saving patient data
  async savePatientData(payload: any, patientClinicDataId: number): Promise<boolean> {
    try {
      const response = await this.esEnrollmentService.savePatientEnrollment(this.userId, patientClinicDataId, payload);
      return response.success;
    } catch (error) {
      console.error('Error saving patient data:', error);
      return false;
    }
  }

  // Delegate to feature service for patient transfer
  async transferPatient(payload: any, patientClinicDataId: number): Promise<{ success: boolean; transfer_id?: number }> {
    try {
      const response = await this.esPatientTransferService.transferPatientToCS(this.userId, patientClinicDataId, payload);
      return {
        success: response.success,
        transfer_id: response.transfer_id
      };
    } catch (error) {
      console.error('Error transferring patient:', error);
      return { success: false };
    }
  }


  // Use proper API service layer
  async checkDeferredTaskPermission(): Promise<boolean> {
    try {
      const response = await usersGraphqlService.getESDeferredTaskPermission(this.hasuraToken!, parseInt(this.userId));
      const data = response.data?.pep_vw_enrollment_specialists;
      if (data && data.length > 0) {
        const specialist = data[0];
        return Boolean(specialist?.deferred_task) || false;
      }
      return false;
    } catch (error) {
      console.error('Error checking deferred task permission:', error);
      return false;
    }
  }

  // Use proper API service layer
  async loadDeferredPatient(): Promise<{ patientClinicDataId: number; patientData: PatientData | null; ccmUpdates: CCMStatusUpdate[] } | null> {
    try {
      // Use VPE deferred patient list query
      const response = await usersGraphqlService.getVPEDeferredList(this.hasuraToken!);
      const data = response.data?.pep_vw_vpe_deferred_list;

      if (data && data.length > 0) {
        const deferredPatient = data[0];
        const deferredPatientId = deferredPatient.pep_patient_clinic_data_id;

        const result = await this.getPatientData();
        // CCM updates removed - enrollment attempts now come from REST API

        return {
          patientClinicDataId: deferredPatientId,
          patientData: result?.patientData || null,
          ccmUpdates: []
        };
      }
      return null;
    } catch (error) {
      console.error('Error loading deferred patient:', error);
      return null;
    }
  }

  // Use workflow assignments to load normal patient (returning from deferred)
  async loadNormalPatient(): Promise<{ patientClinicDataId: number; groupId: number; patientData: PatientData | null; ccmUpdates: CCMStatusUpdate[] } | null> {
    try {
      // Use workflow assignments to get patient data
      const result = await this.getPatientData();
      
      if (!result?.patientData || !result?.assignmentData) {
        console.warn('No patient data found from workflow assignments');
        return null;
      }

      const patientId = result.assignmentData.enrollmentPatientId;
      const groupId = result.assignmentData.userGroupId;

      return {
        patientClinicDataId: patientId,
        groupId: groupId,
        patientData: result.patientData,
        ccmUpdates: []
      };
    } catch (error) {
      console.error('Error loading normal patient:', error);
      return null;
    }
  }

  // Delegate to feature service for invalidating transfer
  async invalidateTransfer(transferId: number): Promise<boolean> {
    try {
      const response = await this.esPatientTransferService.invalidateTransfer(this.userId, transferId);
      return response.success;
    } catch (error) {
      console.error('Error invalidating transfer:', error);
      return false;
    }
  }

  // Get ES names mapping for CCM updates table
  async getESNamesMapping(): Promise<Record<number, string>> {
    try {
      return await this.esEnrollmentService.getESNamesMapping();
    } catch (error) {
      console.error('Error getting ES names mapping:', error);
      return {};
    }
  }
}